import type { ComponentType } from 'react'
import { lazy } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'

import Analytics from './components/Analytics'
import LangLayout from './components/LangLayout'
import ScrollToTop from './components/ScrollToTop'
import publicPages from './data/publicPages.json'
import { initialLanguage } from './i18n'
import AboutPage from './pages/AboutPage'
import ContactPage from './pages/ContactPage'
import CookiesPage from './pages/CookiesPage'
import FaqPage from './pages/FaqPage'
import HomePage from './pages/HomePage'
import IncotermsPage from './pages/IncotermsPage'
import OrderPage from './pages/OrderPage'
import PartnerChinaPage from './pages/PartnerChinaPage'
import PartnersPage from './pages/PartnersPage'
import PrivacyPage from './pages/PrivacyPage'
import ProductsPage from './pages/ProductsPage'
import TermsPage from './pages/TermsPage'

// Route-based code splitting, scoped to the 404 page only. The 9 public
// marketing pages above stay statically imported on purpose: React.lazy's
// Suspense fallback always commits once with nothing in <main> before the
// real page swaps in, and on a page tall enough that the footer sits far
// from where it lands once real content is in (Products, at ~2700px), that
// one extra commit measured as a Lighthouse CLS of 0.687 - a real Core Web
// Vitals regression on exactly the pages Google indexes and ranks by CWV.
// NotFoundPage is noindex (see SeoHead.tsx) so nobody's search ranking
// depends on its CLS - splitting it out still keeps that code out of every
// visitor's main bundle.
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'))

// The single source of truth for "which public pages exist" is
// src/data/publicPages.json - scripts/generate-sitemap.mjs reads the same
// file, so sitemap.xml can never drift from the routes actually rendered
// here. Adding a page means adding it to both this map and that JSON file.
const PAGE_COMPONENTS: Record<string, ComponentType> = {
  '': HomePage,
  about: AboutPage,
  products: ProductsPage,
  partners: PartnersPage,
  'partners/china': PartnerChinaPage,
  'how-to-order': OrderPage,
  'incoterms-logistics': IncotermsPage,
  faq: FaqPage,
  contact: ContactPage,
  terms: TermsPage,
  privacy: PrivacyPage,
  cookies: CookiesPage,
}

function RootRedirect() {
  const location = useLocation()
  const rest = location.pathname === '/' ? '' : location.pathname
  return (
    <Navigate
      to={`/${initialLanguage()}${rest}${location.search}${location.hash}`}
      replace
    />
  )
}

function App() {
  return (
    <>
      <Analytics />
      <ScrollToTop />
      <Routes>
        <Route path="/:lang" element={<LangLayout />}>
          {publicPages.map(({ path }) => {
            const Component = PAGE_COMPONENTS[path]
            return path === '' ? (
              <Route key="index" index element={<Component />} />
            ) : (
              <Route key={path} path={path} element={<Component />} />
            )
          })}
          <Route path="*" element={<NotFoundPage />} />
        </Route>
        <Route path="*" element={<RootRedirect />} />
      </Routes>
    </>
  )
}

export default App
